import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
  registerTab: (value: string, element: HTMLButtonElement) => void;
  unregisterTab: (value: string) => void;
  hoveredTab: string | null;
}

const TabsContext = createContext<TabsContextValue | undefined>(undefined);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs component');
  }
  return context;
};

interface TabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  defaultValue,
  value: controlledValue,
  onValueChange,
  children,
  className = '',
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue || '');
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);

  const handleValueChange = (newValue: string) => {
    // Change tab immediately for instant feedback
    if (controlledValue === undefined) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);

    // Simulate hover effect on intermediate tabs (excluding the target)
    const tabValues = Array.from(tabRefs.current.keys());
    const currentIndex = tabValues.indexOf(value);
    const newIndex = tabValues.indexOf(newValue);

    if (currentIndex !== -1 && newIndex !== -1 && Math.abs(newIndex - currentIndex) > 1) {
      const start = Math.min(currentIndex, newIndex);
      const end = Math.max(currentIndex, newIndex);
      // Get only intermediate tabs (exclude start and end)
      const intermediateTabs = tabValues.slice(start + 1, end);

      // Animate through intermediate tabs only
      intermediateTabs.forEach((tabValue, index) => {
        setTimeout(() => {
          setHoveredTab(tabValue);
        }, (index + 1) * 180); // Start after first delay
      });

      // Clear hover state after animation
      if (intermediateTabs.length > 0) {
        setTimeout(() => {
          setHoveredTab(null);
        }, (intermediateTabs.length + 1) * 180 + 100);
      }
    }
  };

  const registerTab = (tabValue: string, element: HTMLButtonElement) => {
    tabRefs.current.set(tabValue, element);
  };

  const unregisterTab = (tabValue: string) => {
    tabRefs.current.delete(tabValue);
  };

  return (
    <TabsContext.Provider value={{ value, onValueChange: handleValueChange, registerTab, unregisterTab, hoveredTab }}>
      <div className={`w-full ${className}`}>{children}</div>
    </TabsContext.Provider>
  );
};

interface TabsListProps {
  children: React.ReactNode;
  className?: string;
}

export const TabsList: React.FC<TabsListProps> = ({ children, className = '' }) => {
  const { value } = useTabsContext();
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});
  const [isAnimating, setIsAnimating] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!listRef.current) return;

    // Small delay to ensure DOM is ready
    const updateIndicator = () => {
      const activeTab = listRef.current?.querySelector(`button[data-value="${value}"]`) as HTMLButtonElement;

      if (activeTab && listRef.current) {
        const listRect = listRef.current.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();

        // Calculate position relative to container
        const left = tabRect.left - listRect.left;
        const width = tabRect.width;

        // Trigger animation state
        setIsAnimating(true);

        setIndicatorStyle({
          transform: `translateX(${left}px)`,
          width: `${width}px`,
          opacity: 1,
        });

        // Reset animation state after transition
        setTimeout(() => setIsAnimating(false), 900);
      }
    };

    // Update immediately
    updateIndicator();

    // Also update on resize for responsive layouts
    const resizeObserver = new ResizeObserver(updateIndicator);
    if (listRef.current) {
      resizeObserver.observe(listRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [value, children]);

  return (
    <div
      ref={listRef}
      className={`relative inline-flex items-center justify-start gap-1 p-1 bg-muted/50 rounded-lg border border-border ${className}`}
      role="tablist"
    >
      {/* Animated sliding indicator with glow effect during transition */}
      <div
        className={`absolute top-1 bottom-1 left-0 bg-card rounded-md border border-border pointer-events-none ${isAnimating ? 'shadow-lg shadow-primary/20' : 'shadow-sm'
          }`}
        style={{
          ...indicatorStyle,
          transition: 'transform 900ms cubic-bezier(0.4, 0, 0.2, 1), width 900ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-out, box-shadow 900ms ease-out',
        }}
      />
      {children}
    </div>
  );
};

interface TabsTriggerProps {
  value: string;
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
}

export const TabsTrigger: React.FC<TabsTriggerProps> = ({
  value,
  children,
  className = '',
  icon,
}) => {
  const { value: selectedValue, onValueChange, registerTab, unregisterTab, hoveredTab } = useTabsContext();
  const isActive = selectedValue === value;
  const isHovered = hoveredTab === value;
  const tabRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (tabRef.current) {
      registerTab(value, tabRef.current);
    }
    return () => {
      unregisterTab(value);
    };
  }, [value, registerTab, unregisterTab]);

  return (
    <button
      ref={tabRef}
      type="button"
      role="tab"
      aria-selected={isActive}
      data-value={value}
      onClick={() => onValueChange(value)}
      className={`
        relative inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium
        rounded-md transition-all duration-200 ease-out z-10
        focus:outline-none focus-visible:outline-none focus-visible:ring-0
        ${isActive || isHovered
          ? 'text-foreground'
          : 'text-muted-foreground hover:text-foreground hover:scale-105'
        }
        ${className}
      `}
    >
      {icon && (
        <span className={`transition-all duration-200 ${isActive || isHovered ? 'text-primary scale-110' : 'scale-100'}`}>
          {icon}
        </span>
      )}
      <span className="relative z-10">{children}</span>
    </button>
  );
};

interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export const TabsContent: React.FC<TabsContentProps> = ({
  value,
  children,
  className = '',
}) => {
  const { value: selectedValue } = useTabsContext();

  if (selectedValue !== value) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      className={`mt-2 focus:outline-none ${className}`}
      tabIndex={0}
    >
      {children}
    </div>
  );
};

